import React, { useEffect, useMemo } from 'react';
import RcTooltip from 'rc-tooltip';
import moment from 'moment';
import { Button, CircularProgress, Popover, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import actions from '@src/redux/actions';
import { COLOR } from '@src/styles/color';
import useUserInfo from '@src/hooks/useTtsUser';
import Tooltip from '@src/components/Tooltip';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { useTranslation } from 'react-i18next';
import {
  CONVERTING_STATUS,
  REWRITE_STATUS,
  TRANSLATION_STATUS,
} from '@src/constants/project';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { downloadFileByUrl } from '@src/utils/file';
import { EXPORT_TYPE } from '@src/constants/presentation';
import useTrackProjectExported from '@src/hooks/useTrackProjectExported';
import useExportAudio from '@src/hooks/useExportAudio';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import {
  audioExpired,
  getAudioNameSrtAudio,
  getDubbingBlockChanged,
} from '@src/services/project';
import ExportRetentionNote from '../ExportPanel/ExportRetentionNote';
import ExportPanel from '../ExportPanel';
import {
  PopoverButton,
  StyledDownloadMenu,
  PopoverMenuItem,
} from './index.style';

const STRUCTURE_OPTIONS = [
  { label: 'audioFile', value: 'audioFile' },
  { label: 'srtFile', value: 'srtFile' },
];

const DownloadButtonContent = ({ showLoading }) => {
  const { t } = useTranslation();
  const exportHistoryEnabled = useExportHistoryEnabled();
  if (showLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <CircularProgress
          value={75}
          className="circular-progress"
          size="16px"
        />
      </div>
    );
  }
  return t(exportHistoryEnabled ? 'exportFileAction' : 'download');
};

const OverlayComponent = ({
  handleDownload,
  onChangeStructure,
  currentStructure,
  showLoadingInTooltip,
  isDisabledSrt,
  isDisabledAudio,
}) => {
  const { t } = useTranslation();
  const [anchorStructure, setAnchorStructure] = React.useState(null);
  const { audioSrtExpiredAt, audioType } = useSelector(
    (state) => state.project,
  );

  const { ttsUser } = useUserInfo();
  const userAudioRetentionPeriod = ttsUser.retentionPeriod;

  const handleClickStructure = (e) => {
    setAnchorStructure(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorStructure(null);
  };

  const renderArrow = (anchor) => {
    if (anchor) {
      return <ArrowDropUpRoundedIcon width={24} height={24} />;
    }
    return <ArrowDropDownRoundedIcon width={24} height={24} />;
  };

  const showTooltipAudioSrtExpired =
    audioExpired(audioSrtExpiredAt) && currentStructure?.value === 'audioFile';

  const isCurrentDownloadDisabled =
    currentStructure?.value === 'srtFile' ? isDisabledSrt : isDisabledAudio;

  return (
    <StyledDownloadMenu>
      <div className="row">
        <div className="label">{t('fileTypeDownload')}</div>
        <PopoverButton active={!!anchorStructure}>
          <Button className="custom-button" onClick={handleClickStructure}>
            <span className="title-button">
              {t(currentStructure?.label, { format: audioType.toLowerCase() })}
            </span>
            {renderArrow(anchorStructure)}
          </Button>
          <Popover
            open={Boolean(anchorStructure)}
            anchorEl={anchorStructure}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            className="popover-structure"
            PaperProps={{
              style: { borderRadius: '8px' },
            }}
          >
            <div
              style={{
                padding: '8px 0px',
                border: '1px solid #1E164E52',
                borderRadius: '8px',
              }}
            >
              {STRUCTURE_OPTIONS.map((item) => {
                const isItemDisabled =
                  item.value === 'audioFile' ? isDisabledAudio : isDisabledSrt;

                return (
                  <PopoverMenuItem
                    onClick={(e) => {
                      if (isItemDisabled) {
                        e.stopPropagation();
                        return;
                      }
                      onChangeStructure(item);
                      setAnchorStructure(null);
                    }}
                    active={currentStructure?.value === item?.value}
                    disabled={isItemDisabled}
                    key={item.label}
                  >
                    <div className="wrap-label">
                      <Typography variant="body2" className="text">
                        {t(item.label, { format: audioType.toLowerCase() })}
                      </Typography>
                      {currentStructure?.value === item?.value && (
                        <CheckRoundedIcon sx={{ color: COLOR.darkBlue }} />
                      )}
                    </div>
                  </PopoverMenuItem>
                );
              })}
            </div>
          </Popover>
        </PopoverButton>
      </div>
      <ExportRetentionNote />
      <Tooltip
        title={t('noteAudioSrtExpired', {
          numberDays: userAudioRetentionPeriod,
        })}
        arrow
        placement="top"
        disableHoverListener={!showTooltipAudioSrtExpired}
        disableFocusListener={!showTooltipAudioSrtExpired}
      >
        <Button
          className="download-btn"
          variant="contained"
          onClick={() => handleDownload()}
          disabled={showLoadingInTooltip || isCurrentDownloadDisabled}
        >
          <DownloadButtonContent showLoading={showLoadingInTooltip} />
        </Button>
      </Tooltip>
    </StyledDownloadMenu>
  );
};

const DubbingDownloadButton = () => {
  const { getFeatureValue } = useFeatureFlags();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const {
    multiBlock,
    title,
    isDownloading,
    audioType,
    status,
    translation,
    audioSrtExpiredAt,
  } = useSelector((state) => state.project);
  const audioLinkSrt = useSelector((state) => state.project.audioLinkSrt);
  const trackProjectExport = useTrackProjectExported();

  const location = useLocation();
  const projectId = location.pathname.split('/')[2];
  const [currentStructure, setCurrentStructure] = React.useState(
    STRUCTURE_OPTIONS[0],
  );
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const exportHistoryEnabled = useExportHistoryEnabled();
  const srtExport = useExportAudio({
    projectId,
    exportType: EXPORT_TYPE.SRT,
    onSuccess: () => setIsTooltipOpen(false),
  });
  const audioSrtExport = useExportAudio({
    projectId,
    exportType: EXPORT_TYPE.AUDIO_SRT,
    onSuccess: () => setIsTooltipOpen(false),
  });

  const downloadFileSrtEnabled = getFeatureValue(
    FEATURE_KEYS.DOWNLOAD_FILE_SRT,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const { blocks, originalBlocks, blockIds, originalBlockIds } =
    multiBlock || {};
  const isTranslationSuccess =
    translation?.status === TRANSLATION_STATUS.SUCCESS;
  const blockWithsText = Object.values(blocks || {}).filter((block) =>
    isTranslationSuccess ? block?.translatedText : block?.text,
  );

  const dubbingBlocksChanged = useMemo(
    () =>
      getDubbingBlockChanged(
        blockWithsText,
        originalBlocks || {},
        isTranslationSuccess,
      ),
    [blockWithsText, originalBlocks, isTranslationSuccess],
  );

  const isDisabledSrt = blockWithsText.length === 0;

  const isDisabledAudio = useMemo(() => {
    if (!downloadFileSrtEnabled) return true;
    if (!audioLinkSrt) return true;
    if (status !== CONVERTING_STATUS.SUCCESS) return true;
    if (dubbingBlocksChanged?.length > 0) return true;
    if (blockIds?.length !== originalBlockIds?.length) return true;
    if (audioExpired(audioSrtExpiredAt)) return true;
    return false;
  }, [
    downloadFileSrtEnabled,
    audioLinkSrt,
    status,
    dubbingBlocksChanged,
    blockIds,
    originalBlockIds,
    audioSrtExpiredAt,
  ]);

  useEffect(() => {
    if (isDisabledAudio) {
      setCurrentStructure(STRUCTURE_OPTIONS[1]); // srtFile
    } else {
      setCurrentStructure(STRUCTURE_OPTIONS[0]); // audioFile
    }
  }, [isDisabledAudio]);

  const isRewriting = Object.values(blocks || {}).some(
    (block) => block?.rewrite?.status === REWRITE_STATUS.PENDING,
  );

  const isDisabledOuterButton = isDisabledSrt && isDisabledAudio;

  const trackExport = (exportType) => trackProjectExport({ exportType });

  const handleDownloadSrtFile = async () => {
    // The backend builds the file now, so the subtitle download is an export
    // like any other: it is tracked, named by BR_5 and kept in the history.
    if (exportHistoryEnabled) {
      srtExport.startExport();
      return;
    }

    try {
      dispatch(actions.project.setIsDownloading(true));
      const blockToSRT = (block, index) => {
        const content =
          (isTranslationSuccess ? block?.translatedText : block?.text) || '';
        return `${index}\n${block.startTime} --> ${block.endTime}\n${content}\n\n`;
      };

      const srtContent = blockWithsText
        .map((block, index) => blockToSRT(block, index + 1))
        .join('');

      const blob = new Blob([srtContent], {
        type: 'text/plain;charset=utf-8;',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title}.srt`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      trackExport(EXPORT_TYPE.SRT);
    } finally {
      dispatch(actions.project.setIsDownloading(false));
      setIsTooltipOpen(false);
    }
  };

  const handleDownloadAudioSrt = async () => {
    // The backend builds the merged audio now, so this is an export like the
    // subtitle one: tracked, named by BR_5 and kept in the history. With the
    // flag off there is no record to keep it in, and the only merged audio that
    // exists is the one synthesis already produced at `audioLinkSrt`.
    if (exportHistoryEnabled) {
      audioSrtExport.startExport();
      return;
    }

    try {
      dispatch(actions.project.setIsDownloading(true));
      const audioName = `Vbee_${getAudioNameSrtAudio(
        title,
        projectId,
      )}_${moment().unix()}.${audioType}`;
      downloadFileByUrl(audioLinkSrt, audioName);
      trackExport(EXPORT_TYPE.JOIN_AUDIO);
    } finally {
      dispatch(actions.project.setIsDownloading(false));
      setIsTooltipOpen(false);
    }
  };

  const handleDownload = () => {
    const options = {
      srtFile: handleDownloadSrtFile,
      audioFile: handleDownloadAudioSrt,
    };
    return options[currentStructure?.value]();
  };

  // Both exports run on the backend, so they have to feed the same spinner the
  // client-side download drives through `isDownloading`.
  const isBusy =
    isDownloading || srtExport.isExporting || audioSrtExport.isExporting;
  const showLoadingButton = isBusy && !isTooltipOpen;
  const showLoadingInTooltip = isBusy && isTooltipOpen;

  return (
    <RcTooltip
      placement="bottomRight"
      overlay={
        <ExportPanel isOpen={isTooltipOpen}>
          <OverlayComponent
            onChangeStructure={setCurrentStructure}
            handleDownload={handleDownload}
            currentStructure={currentStructure}
            showLoadingInTooltip={showLoadingInTooltip}
            isDisabledSrt={isDisabledSrt}
            isDisabledAudio={isDisabledAudio}
          />
        </ExportPanel>
      }
      trigger={['click']}
      showArrow={false}
      align={{
        offset: [0, 5],
      }}
      onVisibleChange={setIsTooltipOpen}
      visible={isTooltipOpen}
      destroyTooltipOnHide
    >
      <Button
        startIcon={
          !showLoadingButton && (
            <DownloadRoundedIcon style={{ fontSize: '24px' }} />
          )
        }
        className="download-btn"
        variant="contained"
        disabled={
          (isDisabledOuterButton || isRewriting) && !exportHistoryEnabled
        }
        onClick={() => setIsTooltipOpen(!isTooltipOpen)}
      >
        <DownloadButtonContent showLoading={showLoadingButton} />
      </Button>
    </RcTooltip>
  );
};

export default DubbingDownloadButton;
